// AR session lifecycle using the 8th Wall (XR8) engine.
// XR8 manages camera access internally — do NOT call getUserMedia here.

import { logger } from '../utils/logger';
import { appConfig } from '../config/appConfig';
import { imageTargetPipelineModule } from './imageTargetManager';
import { sceneRootPipelineModule } from '../scene/sceneRoot';
import { whaleRevealPipelineModule } from '../scene/whaleReveal';
import { whaleAnimationPipelineModule } from '../scene/whaleAnimation';
import { capturePipelineModule } from './captureModule';
import { getWhaleObject } from '../scene/whaleLoader';

export type SessionStartResult = 'ok' | 'permission-denied' | 'unsupported';

let sessionActive = false;

// ── SLAM tracking status indicator ───────────────────────────────────────────
// Updates the #slam-status-dot element on every reality.trackingstatus event.
function slamStatusIndicatorPipelineModule(): XR8PipelineModule {
  return {
    name: 'slam-status-indicator',
    listeners: [
      {
        event: 'reality.trackingstatus',
        process: ({ detail }: { detail: unknown }) => {
          const { status } = detail as { status: string };
          const dot = document.getElementById('slam-status-dot');
          if (!dot) return;
          dot.dataset.status = status === 'NORMAL' ? 'normal' : 'limited';
        },
      },
    ],
  };
}

// ── SLAM diagnostic module (debug only) ──────────────────────────────────────
// Logs tracking status transitions and pose metrics every 60 frames so we can
// confirm whether SLAM translation and whale distance are changing.
// Status values: UNSPECIFIED | NOT_AVAILABLE | LIMITED | NORMAL
// Reason values: UNSPECIFIED | INITIALIZING | RELOCALIZING | TOO_MUCH_MOTION | NOT_ENOUGH_TEXTURE
function slamDiagnosticPipelineModule(): XR8PipelineModule {
  let frameCount = 0;
  let status = 'UNSPECIFIED';
  let reason = 'UNSPECIFIED';
  let initialThreePosition: Vec3 | null = null;
  let initialRealityPosition: Vec3 | null = null;

  type Vec3 = { x: number; y: number; z: number };
  type XR8Threejs = { Threejs: { xrScene(): { camera: { position: Vec3 } } } };
  type RealityResult = {
    position?: Vec3;
    trackingStatus?: string;
    trackingReason?: string;
    worldPoints?: unknown[];
  };
  type ProcessCpuResult = { reality?: RealityResult };

  const cloneVec = (v: Vec3): Vec3 => ({ x: v.x, y: v.y, z: v.z });
  const distance = (a: Vec3, b: Vec3): number => {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    const dz = a.z - b.z;
    return Math.sqrt(dx * dx + dy * dy + dz * dz);
  };
  const magnitude = (v: Vec3): number => Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z);
  const fmt = (n: number | null | undefined): string =>
    typeof n === 'number' && Number.isFinite(n) ? n.toFixed(3) : '--';
  const fmtVec = (v: Vec3 | null | undefined): string =>
    v ? `${fmt(v.x)}, ${fmt(v.y)}, ${fmt(v.z)}` : '--';
  const updatePanel = (lines: string[]): void => {
    const panel = document.getElementById('ar-debug-panel');
    if (!panel) return;
    panel.innerHTML = lines.map((line) => `<div>${line}</div>`).join('');
  };

  return {
    name: 'slam-diagnostic',
    listeners: [
      {
        event: 'reality.trackingstatus',
        process: ({ detail }: { detail: unknown }) => {
          ({ status, reason } = detail as { status: string; reason: string });
          logger.log(`[SLAM] trackingStatus → status=${status} reason=${reason}`);
        },
      },
    ],
    onUpdate: ({ processCpuResult }: { processCpuResult: unknown }) => {
      const { camera } = (XR8 as unknown as XR8Threejs).Threejs.xrScene();
      const threePosition = cloneVec(camera.position);
      const reality = (processCpuResult as ProcessCpuResult | null)?.reality;
      const realityPosition = reality?.position ? cloneVec(reality.position) : null;
      const whale = getWhaleObject();
      const whalePosition = whale ? cloneVec(whale.position) : null;

      if (!initialThreePosition) initialThreePosition = cloneVec(threePosition);
      if (!initialRealityPosition && realityPosition) {
        initialRealityPosition = cloneVec(realityPosition);
      }

      const threeDelta = distance(threePosition, initialThreePosition);
      const realityDelta =
        realityPosition && initialRealityPosition
          ? distance(realityPosition, initialRealityPosition)
          : null;
      const whaleDistance = whalePosition ? distance(threePosition, whalePosition) : null;
      const featurePoints = Array.isArray(reality?.worldPoints) ? reality.worldPoints.length : null;

      updatePanel([
        `track: ${status} / ${reason}`,
        `three pos: ${fmtVec(threePosition)}`,
        `three delta: ${fmt(threeDelta)} m`,
        `raw pos: ${fmtVec(realityPosition)}`,
        `raw delta: ${fmt(realityDelta)} m`,
        `whale dist: ${fmt(whaleDistance)} m`,
        `whale pos: ${fmtVec(whalePosition)}`,
        `features: ${featurePoints ?? '--'}`,
        `sign width cfg: ${fmt(appConfig.imageTargetPhysicalWidthM)} m`,
      ]);

      if (++frameCount % 60 !== 0) return;

      logger.log(
        `[SLAM] status=${status} reason=${reason}` +
          ` three=(${fmtVec(threePosition)}) |three|=${fmt(magnitude(threePosition))}m` +
          ` threeDelta=${fmt(threeDelta)}m` +
          ` raw=(${fmtVec(realityPosition)}) rawDelta=${fmt(realityDelta)}m` +
          ` whaleDistance=${fmt(whaleDistance)}m` +
          ` featurePoints=${featurePoints ?? '--'}`,
      );
    },
  };
}

/**
 * Initialises the XR8 camera pipeline and starts the AR session.
 * Resolves once the pipeline reports onStart (camera is live).
 *
 * Returns:
 *   'ok'                — pipeline running, camera is live
 *   'permission-denied' — user or OS denied camera access
 *   'unsupported'       — WebGL unavailable or other unrecoverable failure
 */
export function startARSession(canvas: HTMLCanvasElement): Promise<SessionStartResult> {
  if (sessionActive) {
    logger.warn('startARSession called while a session is already active');
    return Promise.resolve('ok');
  }

  return new Promise<SessionStartResult>((resolve) => {
    let settled = false;

    const settle = (result: SessionStartResult) => {
      if (settled) return;
      settled = true;
      if (result === 'ok') sessionActive = true;
      resolve(result);
    };

    const init = async () => {
      XR8.addCameraPipelineModules([
        XR8.GlTextureRenderer.pipelineModule(),
        (XR8 as unknown as { Threejs: { pipelineModule(): unknown } }).Threejs.pipelineModule(),
        XR8.XrController.pipelineModule(),
      ]);

      // Configure image target tracking with the locally generated target data.
      // Fetch the JSON produced by: npx @8thwall/image-target-cli@latest
      // XR8 requires imageTargetData (array of objects), not the cloud imageTargets name API.
      // imagePath must be absolute so XR8 can fetch the luminance image correctly.
      const targetBase = `/assets/targets`;
      const targetData = await fetch(`${targetBase}/${appConfig.imageTargetName}.json`)
        .then((r) => r.json());
      targetData.imagePath = `${targetBase}/${targetData.imagePath}`;
      targetData.physicalWidthInMeters = appConfig.imageTargetPhysicalWidthM;
      // disableWorldTracking must be explicit — some XR8 builds default it to
      // true when imageTargetData is present, which collapses 6DOF SLAM to
      // rotation-only and makes the scene track the camera rather than the world.
      //
      // scale: 'absolute' enables metric scale estimation in the XR8 SLAM
      // engine. Without it, the default responsive scale can preserve 6DOF
      // motion but make real-world translation appear too small or too large
      // relative to meter-based scene placement.
      XR8.XrController.configure({
        imageTargetData: [targetData],
        disableWorldTracking: false,
        scale: 'absolute',
      });

      XR8.addCameraPipelineModule(imageTargetPipelineModule());
      if (appConfig.debug) XR8.addCameraPipelineModule(slamStatusIndicatorPipelineModule());
      XR8.addCameraPipelineModule(sceneRootPipelineModule());
      XR8.addCameraPipelineModule(whaleRevealPipelineModule());
      XR8.addCameraPipelineModule(whaleAnimationPipelineModule());
      XR8.addCameraPipelineModule(capturePipelineModule());

      if (appConfig.debug) {
        XR8.addCameraPipelineModule(slamDiagnosticPipelineModule());
      }

      XR8.addCameraPipelineModule({
        name: 'ar-session-monitor',
        onStart: () => {
          logger.log('XR8 pipeline started');
          settle('ok');
        },
        onException: (error: unknown) => {
          logger.error('XR8 pipeline exception:', error);
          const msg = error instanceof Error ? error.message : String(error);
          if (/permission|denied|notallowed/i.test(msg)) {
            settle('permission-denied');
          } else {
            settle('unsupported');
          }
        },
      });

      XR8.run({ canvas, allowedDevices: XR8.XrConfig.device().ANY });
    };

    if (window.XR8) {
      init();
    } else {
      // { once: true } ensures the listener is auto-removed after first fire.
      window.addEventListener('xrloaded', init, { once: true });
    }
  });
}

/**
 * Stops the active XR8 session.
 * Safe to call if no session is active.
 */
export function stopARSession(): void {
  if (sessionActive) {
    XR8.stop();
    sessionActive = false;
    logger.log('XR8 AR session stopped');
  }
}
